import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import familyHeroImage from "../../assets/images/family-fall-outfit-portrait.webp";
import family9 from "../../assets/images/family-space-themed-outdoor-portrait.webp";
import family10 from "../../assets/images/mother-toddler-golden-hour-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./FamilyArticle.scss";

const lightWindows = [
  {
    label: "MORNING",
    title: "Soft and child-friendly",
    text: "A strong choice for early risers, toddlers and warm seasons before the day becomes hot.",
  },
  {
    label: "GOLDEN HOUR",
    title: "Warm, dimensional light",
    text: "The classic outdoor window, beginning roughly 60–90 minutes before sunset.",
  },
  {
    label: "OVERCAST",
    title: "Flexible and gentle",
    text: "Cloud cover softens shadows and often gives us more freedom with direction and timing.",
  },
];

export const BestTimeOfDayForFamilyPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page family-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Family", to: "/resources/family/" },
          { label: "Best Time of Day for Family Photos" },
        ]}
        category="Family planning guide"
        description="Choose beautiful light while protecting the naps, meals and routines that help your children feel their best."
        imageAlt="Family photographed outdoors in soft evening light"
        imagePosition="center 48%"
        imageSrc={familyHeroImage}
        readingTime="7 min read"
        title="Best Time of Day for Family Photos"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />
      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              For outdoor family photos, the last 60–90 minutes before sunset usually gives the softest, warmest light.
              With young children, a rested and fed child matters more than perfect light, so morning or an earlier
              shaded location may be the better choice.
            </p>
          </div>
        </aside>
        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#planner">Light planner</a>
            </li>
            <li>
              <a href="#golden">Golden hour</a>
            </li>
            <li>
              <a href="#young-kids">Young children</a>
            </li>
            <li>
              <a href="#morning">Morning sessions</a>
            </li>
            <li>
              <a href="#seasons">Seasonal timing</a>
            </li>
            <li>
              <a href="#choose">Choose your time</a>
            </li>
          </ol>
        </nav>
        <section className="newborn-article-section" id="planner">
          <p className="newborn-article-kicker">LIGHT + FAMILY RHYTHM</p>
          <h2>Family Light Planner</h2>
          <p>
            The best window balances the quality of light with the time your family can genuinely enjoy being together.
          </p>
          <div className="family-light-planner">
            {lightWindows.map((item) => (
              <article key={item.label}>
                <span>{item.label}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="newborn-article-section newborn-article-split" id="golden">
          <div>
            <p className="newborn-article-kicker">BEFORE SUNSET</p>
            <h2>Why photographers love golden hour</h2>
            <p>
              When the sun is low, light becomes warmer and shadows soften. We can photograph movement from more
              directions without harsh squinting or deep shadows across faces.
            </p>
            <p>
              The exact start changes throughout the year, so session times move with sunset rather than staying fixed.
            </p>
          </div>
          <ArticlePhoto
            alt="Child in an astronaut costume with parents during an outdoor family session"
            className="newborn-article-split__photo"
            label="PHOTO: GOLDEN HOUR"
            src={family9}
          />
        </section>
        <section className="newborn-article-section" id="young-kids">
          <p className="newborn-article-kicker">CHILDREN COME FIRST</p>
          <h2>Do not sacrifice a happy child for perfect light</h2>
          <p>
            If sunset lands after bedtime, we can choose open shade, begin earlier or schedule a morning. A rested child
            connecting with you will always create stronger photographs than beautiful light with an exhausted child.
          </p>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>Protect the hour before the session</h3>
              <p>Keep the schedule quiet, offer food and avoid arriving directly from an overstimulating event.</p>
            </div>
          </aside>
        </section>
        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="morning">
          <ArticlePhoto
            alt="Mother and toddler playing in warm golden-hour light"
            className="newborn-article-split__photo"
            label="PHOTO: MORNING LIGHT"
            src={family10}
          />
          <div>
            <p className="newborn-article-kicker">AN UNDERRATED OPTION</p>
            <h2>When morning is the better choice</h2>
            <p>
              Morning sessions work beautifully for early risers, babies and toddlers. Parks are often quieter,
              temperatures are cooler and children may have more energy.
            </p>
            <p>
              We choose a location with flattering directional light rather than photographing in direct midday sun.
            </p>
          </div>
        </section>
        <section className="newborn-article-section" id="seasons">
          <p className="newborn-article-kicker">CHARLOTTE THROUGH THE YEAR</p>
          <h2>Season changes the clock</h2>
          <p>
            Summer golden hour can be late, while fall and winter sessions begin much earlier. Spring and fall are
            popular because comfortable temperatures make movement and layers easier. The date, location and your
            children’s routine should be planned together.
          </p>
        </section>
        <section className="newborn-article-section" id="choose">
          <p className="newborn-article-kicker">THE SIMPLE DECISION</p>
          <h2>How we choose your session time</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Start with sunset and the light available at the location.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Protect naps, meals and your youngest child’s happiest window.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Consider heat, crowds and walking distance.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Keep a weather backup when possible.</span>
            </li>
          </ul>
        </section>
        <section className="newborn-article-cta family-article-cta">
          <div>
            <p className="newborn-article-kicker">LIGHT THAT FITS YOUR FAMILY</p>
            <h2>Not sure whether morning or evening is better?</h2>
            <p>Explore the family session options, or contact me with your children’s routine for help choosing a time.</p>
          </div>
          <GuideCtaActions category="family" />
        </section>
      </div>
    </article>
  </main>
);
export default BestTimeOfDayForFamilyPhotos;
